import { useState } from "react";
import { Ruler } from "lucide-react";
import { Field } from "@/components/kit";
import { Stepper } from "@/components/Stepper";
import { formatGal, formatToEighths } from "@/lib/tank";
import { RISER_SHIFT, shrinkGal, stickNeed, thermalKind, tiltFix } from "@/lib/zones";

const KEY = "fhp-chart-tools";
const DEF = { bury: 36, tilt: 0, riser: "center", rackF: 85, tankF: 65 };
export function useChartTools() {
  const [v, setV] = useState(() => { try { return { ...DEF, ...JSON.parse(localStorage.getItem(KEY)) }; } catch { return DEF; } });
  const set = (k) => (x) => setV((o) => { const n = { ...o, [k]: x }; try { localStorage.setItem(KEY, JSON.stringify(n)); } catch { /* full */ } return n; });
  return [v, set];
}

const NEED_TEXT = { ok: "A 12' stick reaches ({n}).", need15: "Grab the 15' stick — this tank needs {n}.", over15: "Even a 15' stick is short — this tank needs {n}." };
export function StickBadge({ tools, d, t }) {
  const { need, level } = stickNeed(tools.bury, d);
  return (
    <div className={`stick-badge stick-${level}`} data-testid="chart-stick-badge">
      <Ruler size={15} />{t(NEED_TEXT[level], { n: formatToEighths(need) })}
    </div>
  );
}

export const BuryInput = ({ tools, set, t }) => (
  <Field label={t("Riser top to tank top (bury depth)")}>
    <Stepper testID="chart-bury" unit="in" step={1} max={120} value={tools.bury} onValue={set("bury")} />
  </Field>
);

const RISERS = [["low", "Low end"], ["center", "Center"], ["high", "High end"]];
export function TiltCard({ s, tools, set, t }) {
  const fix = tiltFix(s.startInches, tools.tilt, tools.riser, s.diameter, s.capacity, s.ends);
  return (
    <div className="tool-body" data-testid="tilt-section">
      <Field label={t("Tilt end to end")}>
        <Stepper testID="chart-tilt" unit="in" decimal step={0.25} max={12} value={tools.tilt} onValue={set("tilt")} />
      </Field>
      <div className="seg" data-testid="chart-riser">
        {RISERS.map(([k, l]) => <button key={k} type="button" className={tools.riser === k ? "seg-on" : ""} onClick={() => set("riser")(k)} data-testid={`chart-riser-${k}`}>{t(l)}</button>)}
      </div>
      <div className="card-sub" data-testid="tilt-result">
        {tools.tilt > 0 ? t("Stick {s} reads like {l} on a level tank ({g} gal).", { s: formatToEighths(s.startInches), l: formatToEighths(fix.level), g: `${fix.gal >= 0 ? "+" : ""}${formatGal(fix.gal)}` })
          : t("Enter the tilt to correct the stick.")}
      </div>
      {tools.tilt > 0 && !fix.valid && <div className="scan-caution">{t("Fuel doesn't reach both ends at this level. The correction is rough here.")}</div>}
      <PitchTable t={t} />
    </div>
  );
}

export const PitchTable = ({ t }) => (
  <table className="mini-table" data-testid="pitch-table">
    <thead><tr><th>{t("Fill riser")}</th><th>{t("Offset per 1\" of tilt")}</th></tr></thead>
    <tbody>{[["low", "Low end"], ["lowq", "¼ from low end"], ["center", "Center"], ["highq", "¼ from high end"], ["high", "High end"]].map(([k, l]) => (
      <tr key={k}><td>{t(l)}</td><td className="mono">{RISER_SHIFT[k] > 0 ? `−${RISER_SHIFT[k]}` : RISER_SHIFT[k] < 0 ? `+${-RISER_SHIFT[k]}` : "0"} in</td></tr>
    ))}</tbody>
  </table>
);

export function ThermalCard({ s, tools, set, t }) {
  const kind = thermalKind(s.product);
  const gal = s.dropGallons || 0;
  return (
    <div className="tool-body" data-testid="thermal-section">
      <div className="tool-2">
        <Field label={t("Rack temp °F")}><Stepper testID="chart-rack-f" step={5} min={-20} max={130} value={tools.rackF} onValue={set("rackF")} /></Field>
        <Field label={t("Tank temp °F")}><Stepper testID="chart-tank-f" step={5} min={-20} max={130} value={tools.tankF} onValue={set("tankF")} /></Field>
      </div>
      <ThermalTable gal={gal} rackF={tools.rackF} tankF={tools.tankF} kind={kind} t={t} />
    </div>
  );
}

export const ThermalTable = ({ gal, rackF, tankF, kind, t }) => (
  <>
    <table className="mini-table" data-testid="thermal-table">
      <thead><tr><th>{t("Product")}</th><th>{t("Per °F")}</th><th>{t("{g} gal drop", { g: formatGal(gal) })}</th></tr></thead>
      <tbody>{["gas", "diesel"].map((k) => (
        <tr key={k} className={k === kind ? "mini-on" : ""}><td>{t(k === "gas" ? "Gasoline" : "Diesel")}</td><td className="mono">{k === "gas" ? "0.069%" : "0.046%"}</td>
          <td className="mono" data-testid={`thermal-${k}`}>{formatGal(Math.abs(shrinkGal(gal, rackF, tankF, k)))} gal {rackF >= tankF ? t("shrink") : t("growth")}</td></tr>
      ))}</tbody>
    </table>
    <div className="card-sub">{t("Warm rack fuel cools in the tank, so the ATG often settles lower 30 minutes after the drop.")}</div>
  </>
);

export const ManifoldNote = ({ t }) => (
  <div className="tool-body card-sub" data-testid="manifold-note">
    {t("Manifolded tanks (siphon bar): the tank you drop into can sit a few inches higher than its twin during a fast dual-hose drop. The siphon evens them out after the drop. Watch the receiving tank's level, not the pair total, and stick both tanks after they settle.")}
  </div>
);
